import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import Footer from "../Footer";
import { isAutheticated } from "../../auth/authhelper";
import { API } from "../../../API";

export default function DataReset() {
    const { token } = isAutheticated();

    const [loading, setLoading] = useState(true);
    const [working, setWorking] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const [status, setStatus] = useState(null);

    const loadStatus = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/api/data-reset/status`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setStatus(res.data);
        } catch (err) {
            console.error("Failed to load data reset status", err);
            Swal.fire({
                icon: "error",
                title: "Could not load status",
                text:
                    err?.response?.data?.message ||
                    "Please check you are signed in as an administrator.",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleArchive = async () => {
        const confirm = await Swal.fire({
            icon: "warning",
            title: "Archive all financial records?",
            html:
                "<div style='text-align:left'>" +
                "This will hide <b>all invoices, click transactions and payments</b> from the application, and reset the invoice number to <b>0001</b>." +
                "<br/><br/>Nothing is deleted. You can restore everything from this same page." +
                "</div>",
            showCancelButton: true,
            confirmButtonText: "Yes, archive now",
            confirmButtonColor: "#d33",
            cancelButtonText: "Cancel",
        });

        if (!confirm.isConfirmed) return;

        setWorking(true);
        try {
            const res = await axios.post(
                `${API}/api/data-reset/archive`,
                { confirmation: confirmText.trim() },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setConfirmText("");
            await loadStatus();
            Swal.fire({
                icon: "success",
                title: "Archived",
                text: res.data?.message || "Records archived.",
            });
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: "Archive failed",
                text: err?.response?.data?.message || "Something went wrong.",
            });
        } finally {
            setWorking(false);
        }
    };

    const handleRestore = async (batchId) => {
        const confirm = await Swal.fire({
            icon: "question",
            title: "Restore this batch?",
            text: `All records archived under ${batchId} will reappear in the application, and the invoice numbering will go back to what it was.`,
            showCancelButton: true,
            confirmButtonText: "Yes, restore",
            cancelButtonText: "Cancel",
        });

        if (!confirm.isConfirmed) return;

        setWorking(true);
        try {
            const res = await axios.post(
                `${API}/api/data-reset/restore/${batchId}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            await loadStatus();
            Swal.fire({
                icon: "success",
                title: "Restored",
                text: res.data?.message || "Records restored.",
            });
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: "Restore failed",
                text: err?.response?.data?.message || "Something went wrong.",
            });
        } finally {
            setWorking(false);
        }
    };

    const armed = confirmText.trim() === "RESET" && !working;

    const summaryCard = (label, count, footLabel, footValue) => (
        <div className="col-md-4 mb-3">
            <div className="card h-100 border">
                <div className="card-body">
                    <h6 className="text-muted mb-2">{label}</h6>
                    <h3 className="mb-0">{Number(count || 0).toLocaleString()}</h3>
                    <p className="text-muted mb-3" style={{ fontSize: "13px" }}>
                        live records
                    </p>
                    <div className="border-top pt-2">
                        <span className="text-muted" style={{ fontSize: "12px" }}>
                            {footLabel}
                        </span>
                        <br />
                        <code style={{ fontSize: "14px" }}>{footValue}</code>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="page-title-box d-flex align-items-center justify-content-between">
                                <h4 className="mb-0">Data Reset</h4>
                                <div className="page-title-right">
                                    <ol className="breadcrumb m-0">
                                        <li className="breadcrumb-item">
                                            <Link to="/dashboard">SoComOTT</Link>
                                        </li>
                                        <li className="breadcrumb-item active">Data Reset</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="card">
                            <div className="card-body text-center py-5">
                                <div className="spinner-border text-primary" role="status" />
                                <p className="mt-3 mb-0 text-muted">Loading current state…</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="card">
                                <div className="card-body">
                                    <p className="text-muted">
                                        Archiving hides records from every screen in the
                                        application and resets the numbering. Nothing is deleted —
                                        every archived record stays in the database and can be
                                        brought back using the restore button below.
                                    </p>

                                    <div className="row mt-3">
                                        {summaryCard(
                                            "Invoices",
                                            status?.live?.invoices,
                                            "Next invoice number",
                                            status?.nextInvoiceNumber || "—"
                                        )}
                                        {summaryCard(
                                            "Click Transactions",
                                            status?.live?.clicks,
                                            "Next serial number",
                                            status?.nextClickSerial || "—"
                                        )}
                                        {summaryCard(
                                            "Payments",
                                            status?.live?.payments,
                                            "Totals after reset",
                                            "0.00"
                                        )}
                                    </div>

                                    <div
                                        className="p-3 mt-2"
                                        style={{
                                            background: "#fdf1f1",
                                            border: "1px solid #f2c6c6",
                                            borderRadius: "6px",
                                        }}
                                    >
                                        <h6 className="text-danger mb-1">
                                            Archive all three together
                                        </h6>
                                        <p
                                            className="text-danger mb-2"
                                            style={{ fontSize: "13px" }}
                                        >
                                            Invoices, click transactions and payments are archived
                                            as one batch so they can be restored together. Type{" "}
                                            <b>RESET</b> below to enable the button.
                                        </p>
                                        <div className="d-flex" style={{ gap: "10px" }}>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="RESET"
                                                value={confirmText}
                                                onChange={(e) => setConfirmText(e.target.value)}
                                                style={{ maxWidth: "260px" }}
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-danger"
                                                disabled={!armed}
                                                onClick={handleArchive}
                                            >
                                                {working ? "Working…" : "Archive now"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card">
                                <div className="card-body">
                                    <h5 className="mb-3">Archive History</h5>

                                    {!status?.batches?.length ? (
                                        <p className="text-muted mb-0">
                                            Nothing has been archived yet.
                                        </p>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-bordered mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>Batch</th>
                                                        <th>Archived On</th>
                                                        <th>By</th>
                                                        <th>Contents</th>
                                                        <th>Status</th>
                                                        <th className="text-center">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {status.batches.map((b) => (
                                                        <tr key={b.batchId}>
                                                            <td>
                                                                <code>{b.batchId}</code>
                                                            </td>
                                                            <td>
                                                                {new Date(
                                                                    b.createdAt
                                                                ).toLocaleString()}
                                                            </td>
                                                            <td>{b.createdBy || "—"}</td>
                                                            <td style={{ fontSize: "13px" }}>
                                                                {Number(
                                                                    b.counts?.invoices || 0
                                                                ).toLocaleString()}{" "}
                                                                invoices,{" "}
                                                                {Number(
                                                                    b.counts?.clicks || 0
                                                                ).toLocaleString()}{" "}
                                                                clicks,{" "}
                                                                {Number(
                                                                    b.counts?.payments || 0
                                                                ).toLocaleString()}{" "}
                                                                payments
                                                            </td>
                                                            <td>
                                                                {b.status === "restored" ? (
                                                                    <span className="badge badge-success">
                                                                        Restored
                                                                    </span>
                                                                ) : (
                                                                    <span className="badge badge-warning">
                                                                        Archived
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="text-center">
                                                                {b.status === "restored" ? (
                                                                    <span className="text-muted">
                                                                        —
                                                                    </span>
                                                                ) : (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        disabled={working}
                                                                        onClick={() =>
                                                                            handleRestore(b.batchId)
                                                                        }
                                                                    >
                                                                        Restore batch
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <Footer />
        </div>
    );
}
